import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

import firebase from '../core/libs/Firebase';

import { TAppProduct, TAuthHeader, TUserAgent } from 'TProtocol/common/models';
import { TWebAuthenticateStatusResponse } from 'TProtocol/authentication/messages';

export interface IUUserContext {
  cookiesLoaded: boolean;
  userId: string;
  authToken: string;
  selfContactId: string;
  lastLoggedIn: number;

  isLoggedIn: () => boolean;
  getSecondsSinceLogin: () => number;
  getAuthHeader: () => TAuthHeader;
  handleLogin: (response: TWebAuthenticateStatusResponse) => void;
  handleSignOut: () => void;
}

function detectBrowser() {
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
  if ((navigator.userAgent.indexOf('Opera') || navigator.userAgent.indexOf('OPR')) != -1) {
    return 'Opera';
  } else if (navigator.userAgent.indexOf('Chrome') != -1) {
    return 'Chrome';
  } else if (navigator.userAgent.indexOf('Safari') != -1) {
    return 'Safari';
  } else if (navigator.userAgent.indexOf('Firefox') != -1) {
    return 'Firefox';
  } else if ((navigator.userAgent.indexOf('MSIE') != -1) || (!!document.DOCUMENT_NODE)) {
    return 'IE';
  } else {
    return 'Unknown';
  }
}

function detectOS() {
  // https://www.geeksforgeeks.org/detect-the-operating-system-of-user-using-javascript/
  if (navigator.userAgent.indexOf('Win') != -1) {
    return 'Windows OS';
  } else if (navigator.userAgent.indexOf('Mac') != -1) {
    return 'Macintosh';
  } else if (navigator.userAgent.indexOf('Linux') != -1) {
    return 'Linux OS';
  } else if (navigator.userAgent.indexOf('Android') != -1) {
    return 'Android OS';
  } else if (navigator.userAgent.indexOf('like Mac') != -1) {
    return 'iOS';
  } else {
    return 'Unknown OS';
  }
}

const UserContext = createContext<IUUserContext | null>(null);

export function UserContextProvider(props: { children: ReactNode }) {
  const [userId, setUserId] = useState<string>('');
  const [authToken, setAuthToken] = useState<string>('');
  const [selfContactId, setSelfContactId] = useState<string>('');
  const [lastLoggedIn, setLastLoggedIn] = useState<number>(0);
  const [cookiesLoaded, setCookiesLoaded] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies(
    ['userId', 'authToken', 'selfContactId', 'firebaseAuth', 'lastLoggedIn']
  );

  useEffect(() => {
    setUserId(cookies.userId);
    setAuthToken(cookies.authToken);
    setSelfContactId(cookies.selfContactId);
    setLastLoggedIn(cookies.lastLoggedIn);
    setCookiesLoaded(true);
  }, [cookies]);

  const isLoggedIn = () => {
    return !!(authToken && selfContactId && lastLoggedIn && userId);
  };

  const handleSignOut = async () => {
    handleCookies();
  };

  const handleCookies = () => {
    removeCookie('userId');
    removeCookie('authToken');
    removeCookie('selfContactId');
    removeCookie('firebaseAuth');
    removeCookie('lastLoggedIn');
    setUserId('');
    setAuthToken('');
    setSelfContactId('');
    setLastLoggedIn(-1);
  };

  const getAuthHeader = (): TAuthHeader => {
    return new TAuthHeader({
      userId: userId,
      accessToken: authToken,
      timestamp: Math.floor(new Date().getTime() / 1000),
      deviceId: 'web',
      agent: new TUserAgent({
        platform: 'web',
        version: '0.0.0.2',
        deviceVersion: detectBrowser(),
        osVersion: detectOS(),
        product: TAppProduct.MOUSE
      })
    });
  };

  const getSecondsSinceLogin = (): number => {
    if (lastLoggedIn > 0) {
      return Math.floor((new Date().getTime() - lastLoggedIn) / 1000);
    } else {
      return -1;
    }
  };

  const handleLogin = async (response: TWebAuthenticateStatusResponse) => {
    const authResponse = response.authResponse;

    if (authResponse && authResponse.accessToken) {
      // success, store this in cookie and state.
      setCookie('userId', authResponse.userId);
      setCookie('authToken', authResponse.accessToken);
      setCookie('selfContactId', authResponse.contactId);

      setUserId(authResponse.userId);
      setAuthToken(authResponse.accessToken);
      setSelfContactId(authResponse.contactId);

      try {
        await firebase.auth().signInWithCustomToken(authResponse.customFirebaseToken)
        setCookie('firebaseAuth', authResponse.customFirebaseToken);
        const loggedInTimestamp = new Date().getTime();

        setCookie('lastLoggedIn', loggedInTimestamp);
        setLastLoggedIn(loggedInTimestamp);
      } catch (e) {
        return;
      }
    }
  };

  const context: IUUserContext = {
    cookiesLoaded,
    userId,
    authToken,
    selfContactId,
    lastLoggedIn,
    isLoggedIn,
    getSecondsSinceLogin,
    getAuthHeader,
    handleLogin,
    handleSignOut
  };

  return (
    <UserContext.Provider value={context}>
      {props.children}
    </UserContext.Provider>
  );
}

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
